import React, {createContext,ReactNode,Dispatch,SetStateAction,useCallback,useContext,useMemo,useState,startTransition, useEffect,
} from "react";
import { AppContext } from "./AppContext";
import { baseDays, Coordonates, extractLastWord, formatDateToYMD, isBetween, listCuisine, Location, Restaurant, TypeResto } from "../components/types/types";


const toMinutes = (h: unknown): number => {
  if (typeof h !== "string") {
    return NaN;
  }
  const m = h.trim().match(/^(\d{1,2})h(\d{2})$/);
  if (!m) return NaN;

  const hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  return hh * 60 + mm;
};

const nowMinutes = (): number => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
};
const getRangesForDate = (restaurant: Restaurant, date: Date): Array<[number, number]> => {
    const ymd = formatDateToYMD(date);
    const dayKey = baseDays[date.getDay()];
    const special = restaurant.special_date?.[ymd];
    if (special) {
        const isOpen = special?.[0];
        if (isOpen !== true) return [];

        const slots = special?.[1];
        if (!Array.isArray(slots) || !Array.isArray(slots[0])) return [];

        return (slots as any[])
            .map((r) => [toMinutes(r?.[0]), toMinutes(r?.[1])] as [number, number])
            .filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e));
    }
    const sched = restaurant.schedule?.[dayKey];
    if (!Array.isArray(sched)) return [];
    const ranges = sched.map((r) => [toMinutes(r[0]), toMinutes(r[1])] as [number, number]).filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e));
    return ranges;
};
const isStillValidForServiceFilter = (restaurant: Restaurant,service: "midi" | "soir"): boolean => {
    const today = new Date();
    const ranges = getRangesForDate(restaurant, today);
    if (!ranges.length) return false;
    const target = service === "midi" ? toMinutes("12h30") : toMinutes("20h00");
    const chosen =
    ranges.find(([s, e]) => s <= target && target <= e) ??
    (service === "midi" ? ranges[0] : ranges[ranges.length - 1]);

    const end = chosen[1];
    const now = nowMinutes();
    return now <= end;
};

type RestaurantContextType = {
    priceFilter: (min: number, max: number) => void;
    fetchRestaurants:() => Promise<void>,
    distanceFilter: (distance: number,functionReplace?:() => void) => void,
    fetchResto:boolean,
    setFetchResto:Dispatch<SetStateAction<boolean>>,
    listResto:Restaurant[],
    setListResto:Dispatch<SetStateAction<Restaurant[]>>,
    listRestoHome:Restaurant[],
    setListRestoHome:Dispatch<SetStateAction<Restaurant[]>>,
    listRestoAll: Restaurant[];
    setListRestoAll: Dispatch<SetStateAction<Restaurant[]>>;

    listRestoFiltered: Restaurant[];
    setListRestoFiltered: Dispatch<SetStateAction<Restaurant[]>>;
    listRestoBackup: Restaurant[];
    setListRestoBackup: Dispatch<SetStateAction<Restaurant[]>>;

};

const RestaurantContext = createContext<RestaurantContextType>({
    priceFilter: () => {},
    fetchRestaurants:async () => {},
    distanceFilter: (_distance: number, _fn?: () => void) => {},
    fetchResto: false,
    setFetchResto: () => {},
    listResto:[],
    setListResto:() => {},
    listRestoHome:[],
    setListRestoHome:() => {},

    listRestoAll: [],
    setListRestoAll: () => {},

    listRestoFiltered: [],
    setListRestoFiltered: () => {},
    listRestoBackup: [],
    setListRestoBackup: () => {},

});


export const RestaurantContextProvider = ({ children }: { children: ReactNode }) => {
    const { setFilterChoose, setLocations, setAllPresentFilter,filterChoose,hour_selected,setHour_selected,date_selected,setDate_selected  } = useContext(AppContext);

    const [fetchResto,setFetchResto] = useState(false)
    const [listResto,setListResto] = useState<Restaurant[]>([])
    const [listRestoHome,setListRestoHome] = useState<Restaurant[]>([])

    const [listRestoAll, setListRestoAll] = useState<Restaurant[]>([]);
    const [listRestoFiltered, setListRestoFiltered] = useState<Restaurant[]>([]);
    const [listRestoBackup, setListRestoBackup] = useState<Restaurant[]>([]);

    const filterRestaurants = (restaurants: Restaurant[]): Restaurant[] => {
        if (filterChoose.length === 0) return restaurants;

        const filterCuisines = filterChoose.filter(filter => typeof filter === 'string' && listCuisine.includes(filter));
        
        const filterTypes = filterChoose.filter(filter => typeof filter === 'string' && TypeResto.includes(filter))
        const filterTypesSecond = filterTypes.map(type => extractLastWord(type as string));


        const filteredByCuisine = restaurants.filter(restaurant => 
            filterCuisines.some(cuisine => restaurant.cuisines.includes(cuisine as string))
        );

        const filteredByType = restaurants.filter(restaurant => 
            filterTypes.some(type => restaurant.type[0] === type ||  (restaurant.type.length > 1 && extractLastWord(restaurant.type[1]) === extractLastWord(type as string)))
        );

        const uniqueRestaurants = [...restaurants,...filteredByCuisine,...filteredByType].filter((restaurant, index, self) => 
            index === self.findIndex((r) => r.idrestaurant === restaurant.idrestaurant )
        );

        return uniqueRestaurants.filter(restaurant => {
                let countCuisine = 0;
                let countType = 0;
                if(filterCuisines.includes(restaurant.cuisines[0]) || filterCuisines.includes(restaurant.cuisines[1])) countCuisine += 1
                if(filterTypes.includes(restaurant.type[0]) ||  (restaurant.type.length > 1 && filterTypesSecond.includes(extractLastWord(restaurant.type[1])))) countType += 1
                return filterChoose.every(filter => {
                    if (typeof filter === 'string') {
                        if(listCuisine.includes(filter)) {          
                            if(countCuisine>0) return true                
                        }
                        if(TypeResto.includes(filter)) {          
                            if(countType>0) return true                
                        }
                        if(['Ouvert ce midi', 'Ouvert ce soir', 'A proximité'].includes(filter)) {
                            if('Ouvert ce midi' === filter) {
                                return isStillValidForServiceFilter(restaurant, "midi");
                            }
                            if ('Ouvert ce soir' === filter) {
                                return isStillValidForServiceFilter(restaurant, "soir");
                            }
                            return true
                        }
                        return (
                            (['5 étoiles', '4 étoiles', '3 étoiles'].includes(filter) &&
                                restaurant.note >= parseFloat(filter[0])) ||
                                    (restaurant.filters?.includes(filter))
                        );
                    }
                    else if (Array.isArray(filter) ) {
                        const nbr0 = filter[0]
                        const nbr1 = filter[1]
                        return (nbr0 <= restaurant.total_price && restaurant.total_price <= nbr1)
                    }
                    return true
                    
                })
        });
    };



    const changementSchedule_Date =() => {
        if(hour_selected !== '') {
            if(date_selected) {
                const formattedDate = formatDateToYMD(date_selected)
                const prevList = listResto.filter((elem) => {
                    if(elem.special_date) { 
                        if(elem.special_date[formattedDate]) { 
                            if(elem.special_date[formattedDate][0]) {
                                if(Array.isArray(elem.special_date[formattedDate][1])) {
                                    if(isBetween(elem.special_date[formattedDate][1][0][0],elem.special_date[formattedDate][1][0][1],hour_selected)) return elem
                                    else if(elem.special_date[formattedDate][1].length > 1 && isBetween(elem.special_date[formattedDate][1][1][0],elem.special_date[formattedDate][1][1][1],hour_selected)) return elem
                                }
                                else return 
                            }  
                            else return
                        }
                    }
                    
                    if(Array.isArray(elem.schedule[baseDays[date_selected.getDay()]])) {
                        if(isBetween(elem.schedule[baseDays[date_selected.getDay()]][0][0],elem.schedule[baseDays[date_selected.getDay()]][0][1],hour_selected)) return elem
                        else if(elem.schedule[baseDays[date_selected.getDay()]].length > 1 && isBetween(elem.schedule[baseDays[date_selected.getDay()]][1][0],elem.schedule[baseDays[date_selected.getDay()]][1][1],hour_selected)) return elem
                    }
                    else return 
                    
                })
                const filteredList = filterRestaurants(prevList);
                return filteredList;
            }
            else {
                const today = new Date()
                const formattedDate = formatDateToYMD(today)
                const prevList = listResto.filter((elem) => {
                    if(elem.special_date) { 
                        if(elem.special_date[formattedDate]) { 
                            if(elem.special_date[formattedDate][0]) return elem   
                            else return
                        }
                    }   
                    if(Array.isArray(elem.schedule[baseDays[today.getDay()]])) {
                        if(isBetween(elem.schedule[baseDays[today.getDay()]][0][0],elem.schedule[baseDays[today.getDay()]][0][1],hour_selected)) return elem
                        else if(elem.schedule[baseDays[today.getDay()]].length > 1 && isBetween(elem.schedule[baseDays[today.getDay()]][1][0],elem.schedule[baseDays[today.getDay()]][1][1],hour_selected)) return elem
                    }
                    else return 
                    
                })
                const filteredList = filterRestaurants(prevList);
                return filteredList;
            }
        }
        else if(date_selected) {
        
            const formattedDate = formatDateToYMD(date_selected)
            const prevList = listResto.filter((elem) => {
                if(elem.special_date) { 
                    if(elem.special_date[formattedDate]) { 
                        if(elem.special_date[formattedDate][0]) return elem   
                        else return
                    }
                }
                if(Array.isArray(elem.schedule[baseDays[date_selected.getDay()]])) return elem
                
            })
            const filteredList = filterRestaurants(prevList);
            return filteredList;
        }
        else {
            const filteredList = filterRestaurants(listResto);
            return filteredList;
        }
    } 

    useEffect(() => {
        if(filterChoose.includes('Ouvert ce midi')) {
            if(filterChoose.includes('Ouvert ce soir')) {
                setFilterChoose(filterChoose.filter((elem) => elem !== 'Ouvert ce soir'))
                return;
            }
            setDate_selected(new Date())
            setHour_selected('12h30')
        }
        else if(filterChoose.includes('Ouvert ce soir')) {
            if(filterChoose.includes('Ouvert ce midi')) {
                setFilterChoose(filterChoose.filter((elem) => elem !== 'Ouvert ce midi'))
                return
            }
            setDate_selected(new Date())
            setHour_selected('20h00') 
        }
        const filteredList = changementSchedule_Date();

        startTransition(() => {
            setListRestoFiltered(filteredList);
            setListRestoBackup(filteredList);
        })  
    }, [filterChoose]); 

    useEffect(() => {
        const filteredList = changementSchedule_Date()
        startTransition(() => {
            setListRestoFiltered(filteredList);
            setListRestoBackup(filteredList);
        })
        const viewResto = document.getElementById('mainCorpRestau');
        viewResto?.scrollTo(0, 0);
    },[listResto]) 

    useEffect(() => {
        const list = changementSchedule_Date()
        startTransition(() => {
            setListRestoFiltered(list); 
            setListRestoBackup(list);
        })
    },[hour_selected,date_selected])  


    const priceFilter = useCallback((min:number,max:number) => {
        startTransition(() => {
            setFilterChoose((prev) => {
                let updatedFilters = [...prev];
                const index = updatedFilters.findIndex(filter => 
                    Array.isArray(filter) && filter.length === 2 && 
                    filter.every(num => typeof num === "number")
                );
                if (index !== -1) updatedFilters[index] = [min, max];
                else updatedFilters.push([min, max]);
                return updatedFilters
            })
        })
    },[setFilterChoose])
    const recupAllFilter = useCallback((list:Restaurant[]) => {
        const allFilters = new Set<string>();
        list.forEach((resto) => {
            resto.filters?.forEach((filtre) => allFilters.add(filtre));
            resto.cuisines.forEach((cuisine) => cuisine !== '' && allFilters.add(cuisine));
        });
        setAllPresentFilter(Array.from(allFilters));
    },[setAllPresentFilter])
    const fetchRestaurants = useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_DIRECTION_REQUEST}/api/getResto`, { 
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data: Restaurant[] = await response.json();
            startTransition(() => {
                setListResto(data);
                setListRestoHome(data);

                setListRestoAll(data); 

                recupAllFilter(data)
                setFetchResto(true)
            })
        } catch (error) {
            console.error("Error fetching restaurant data:", error);
        }
    },[setLocations, recupAllFilter]);

    const distanceFilter =  useCallback((distance:number,functionReplace?: () => void) => {
        try {
            if (!navigator.geolocation) {
                alert("La géolocalisation n'est pas supportée par votre navigateur.");
                return;
            }
            navigator.geolocation.getCurrentPosition(

                async (position) => {
                    startTransition(() => {
                        setFilterChoose(prev => {
                            const idx = prev.findIndex(f => typeof f === "number");
                            if (idx !== -1) {
                                const copy = [...prev];
                                copy[idx] = distance;
                                return copy;
                            }
                            return [...prev.filter((elem) => elem !== 'A proximité'), distance];
                        });
                    });
                    const { latitude, longitude } = position.coords;
                    const response = await fetch(`${process.env.REACT_APP_DIRECTION_REQUEST}/api/getRestoCoords`, { 
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            latitude: latitude,
                            longitude: longitude,
                            distance:distance,
                        }),
                    });
                    const data: Restaurant[] = await response.json();
                    setListResto(data);
                    startTransition(() => {
                        setListRestoAll(data);
                        recupAllFilter(data)
                        setFetchResto(true)
                        if(functionReplace) functionReplace()
                    })
                }, (error) => {
                    alert("La géolocalisation est désactivée ou non autorisée. Veuillez l'activer pour filtrer par distance.");
                }
                );
        } catch (error) {

            alert("La géolocalisation est désactivée ou non autorisée. Veuillez l'activer pour filtrer par distance.");
        }
    },[setFilterChoose, setLocations, recupAllFilter])

    useEffect(() => {
        const data = listResto;
        const newLocations = data.map((resto) => {
            const [lng, lat] = resto.location.coordinates;
            return {
            lat,
            lng,
            name: resto.name,
            idrestaurant: resto.idrestaurant,
            id_url: resto.id_url,
            devanture: resto.devanture,
            type: resto.type,
            note: resto.note,
            address_hint: resto.address_hint || "",
            address: resto.address,
            phone: resto.phone,
            listImg: resto.listimg ? resto.listimg : [],
            schedule: resto.schedule,
            date_update: resto.date_update,
            is_resa_phone: resto.is_resa_phone,
            nbr_review: resto.nbr_review || 0,
            filters: resto.filters || [],
            special_date: resto.special_date ? resto.special_date : {},
            biography: resto.biography,
            total_price: resto.total_price,
            cuisines: resto.cuisines,
            date_published: resto.date_published ? resto.date_published : null,
            };
        });
        startTransition(() => {
            setListRestoAll(data); 
            setLocations(newLocations);
            recupAllFilter(data);
        });
    }, [listResto , setLocations, recupAllFilter]);


  const value = useMemo(
    () => ({
        priceFilter,
        fetchRestaurants,
        distanceFilter,
        fetchResto,setFetchResto,
        listResto,setListResto,
        listRestoHome,setListRestoHome,
        listRestoAll,setListRestoAll,
        listRestoFiltered,setListRestoFiltered,
        listRestoBackup,setListRestoBackup
    }),
    [
        priceFilter,
        fetchRestaurants,
        distanceFilter,
        fetchResto,
        listResto,
        listRestoHome,
        listRestoAll,
        listRestoFiltered,
        listRestoBackup
    ]
  );

  return <RestaurantContext.Provider value={value}>{children}</RestaurantContext.Provider>;
};

export { RestaurantContext };
